import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import SiteHeader from "../components/SiteHeader.jsx";
import SiteFooter from "../components/SiteFooter.jsx";
import { useAuth } from "../lib/auth.jsx";
import analytics from "../lib/analytics";
import {
  BookOpen, Pill, ShieldAlert, Calculator, Search, Star,
  CheckCircle2, ArrowRight, Activity, Stethoscope, Clock, Globe, Heart, WifiOff, Flame, Download, FileText, ShieldCheck
} from "lucide-react";

// Animated count-up that fires once the element scrolls into view.
function CountUp({ value, suffix = "", className = "" }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const done = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !done.current) {
        done.current = true;
        const target = value;
        const dur = 1100, t0 = performance.now();
        const tick = (t) => {
          const p = Math.min(1, (t - t0) / dur);
          const eased = 1 - Math.pow(1 - p, 3);
          setDisplay(Math.round(target * eased));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [value]);
  return <span ref={ref} className={className}>{display.toLocaleString("en-ZA")}{suffix}</span>;
}


const features = [
  { icon: BookOpen, title: "Clinical Protocols", text: "500+ SA protocols from NDoH, SEMDSA, Stroke SA and more. Red flags, ICD-10 and counselling, indexed by specialty." },
  { icon: Pill, title: "Drug Reference", text: "395+ drugs, SAMF-aligned. Dosing, renal adjustment, pregnancy notes and red-flag adverse effects at a glance." },
  { icon: ShieldAlert, title: "Interaction Checker", text: "780+ interaction pairs with severity grading, clinical effect and clear management steps." },
  { icon: Calculator, title: "Medical Calculators", text: "32+ bedside calculators: eGFR, CURB-65, HEART, CHA₂DS₂-VASc, Wells', MELD-Na, CIWA, QTc and more." },
  { icon: Flame, title: "Burns TBSA Calculators", text: "Interactive body diagrams. Rule of Nines plus paediatric Lund & Browder with Parkland fluids." },
  { icon: Heart, title: "Paediatric Dosing", text: "111 weight-based paediatric doses, plus fluids, resus and emergency quick cards." },
  { icon: ShieldCheck, title: "Antibiotic Guide", text: "60+ empiric guides: first-line, alternatives, doses and durations, aligned to SA STG/EML." },
  { icon: Stethoscope, title: "Quick References", text: "58 rapid panels: DKA, sepsis, ACLS, RSI, transfusion, immunisations, antidotes and more." },
  { icon: FileText, title: "ICD-10 Code Finder", text: "Search 320+ SA-tariff codes with assisted natural-language lookup. Reference only." },
  { icon: Search, title: "Unified Search", text: "One prompt across protocols, drugs, calculators and guides. Save favorites and recents." },
  { icon: WifiOff, title: "Works Offline", text: "Installable app. Core protocols, drugs and calculators cached for offline use." },
];

const specialties = [
  "Emergency", "HIV", "TB", "Cardiology", "Endocrinology", "Paediatrics",
  "Obstetrics", "Respiratory", "Critical Care", "Neurology"
];

export default function Landing() {
  const nav = useNavigate();
  const { user } = useAuth();
  const startTrial = () => { if (!user) analytics.signupStart("/signup"); nav("/signup"); };
  const [counts, setCounts] = useState(null);
  useEffect(() => {
    import("../lib/api").then(({ default: api }) => {
      api.get("/stats/counts").then((r) => setCounts(r.data)).catch(() => {});
    });
  }, []);

  // Rotating "Live Protocol Preview" — cycles through a few protocols so the hero
  // card feels alive instead of being stuck on one condition.
  const PREVIEW_PROTOCOLS = [
    {
      tag: "Critical · Emergency", tagColor: "text-rose-600", badge: "bg-rose-50 text-rose-700 border-rose-200",
      title: "Anaphylaxis Management (Adult)", source: "SA Allergic Society Guidelines",
      steps: [
        { t: "Adrenaline IM", d: "0.5 mg (0.5 mL of 1:1000) anterolateral mid-thigh. Repeat q5min." },
        { t: "Position & Oxygen", d: "Supine, legs elevated, 100% O₂ via NRB mask." },
        { t: "IV Access & Fluids", d: "Two large-bore lines. 1–2 L crystalloid bolus." },
        { t: "Second-line", d: "Salbutamol, hydrocortisone 200 mg IV, chlorphenamine." },
      ],
    },
    {
      tag: "Emergency · Cardiac", tagColor: "text-rose-600", badge: "bg-rose-50 text-rose-700 border-rose-200",
      title: "Adult Cardiac Arrest (ACLS)", source: "SA Resuscitation Council",
      steps: [
        { t: "High-quality CPR", d: "100–120/min, depth 5–6 cm, minimise interruptions." },
        { t: "Rhythm check", d: "Shockable (VF/pVT) → defibrillate 150–200 J biphasic." },
        { t: "Adrenaline", d: "1 mg IV every 3–5 min; amiodarone 300 mg after 3rd shock." },
        { t: "Reversible causes", d: "Treat the 4 Hs & 4 Ts." },
      ],
    },
    {
      tag: "Endocrine · Emergency", tagColor: "text-amber-600", badge: "bg-amber-50 text-amber-700 border-amber-200",
      title: "Diabetic Ketoacidosis (Adult)", source: "SEMDSA / NDoH STG-EML 2024",
      steps: [
        { t: "Fluids first", d: "0.9% saline 1 L over 1st hour, then titrate." },
        { t: "Insulin infusion", d: "Soluble insulin 0.1 U/kg/hr IV after fluids." },
        { t: "Potassium", d: "Add 20–40 mmol KCl/L once K <5.3 & urine output." },
        { t: "Monitor", d: "Hourly glucose, K, ketones/VBG; add dextrose at <14." },
      ],
    },
    {
      tag: "Obstetric · Emergency", tagColor: "text-pink-600", badge: "bg-pink-50 text-pink-700 border-pink-200",
      title: "Eclampsia & Severe Pre-eclampsia", source: "NDoH Maternity Care Guidelines",
      steps: [
        { t: "Magnesium sulphate", d: "4 g IV loading over 20 min, then 1 g/hr infusion." },
        { t: "Control BP", d: "Labetalol / nifedipine / hydralazine — target <160/110." },
        { t: "Airway & seizure", d: "Left lateral, protect airway, O₂; recurrent fit → 2 g MgSO₄." },
        { t: "Deliver", d: "Stabilise mother, then expedite delivery." },
      ],
    },
    {
      tag: "Infectious · Emergency", tagColor: "text-rose-600", badge: "bg-rose-50 text-rose-700 border-rose-200",
      title: "Septic Shock", source: "Surviving Sepsis / Adult Hospital STG 2024",
      steps: [
        { t: "Sepsis Six (1 hr)", d: "O₂, cultures, IV broad-spectrum antibiotics, fluids, lactate, UO." },
        { t: "Fluid resuscitation", d: "30 mL/kg crystalloid for hypotension/lactate ≥4." },
        { t: "Vasopressors", d: "Noradrenaline if MAP <65 after fluids." },
        { t: "Source control", d: "Identify & treat the source; reassess perfusion." },
      ],
    },
  ];
  const [previewIdx, setPreviewIdx] = useState(0);
  const [previewFade, setPreviewFade] = useState(true);
  useEffect(() => {
    const id = setInterval(() => {
      setPreviewFade(false);
      setTimeout(() => {
        setPreviewIdx((i) => (i + 1) % PREVIEW_PROTOCOLS.length);
        setPreviewFade(true);
      }, 350);
    }, 4200);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const preview = PREVIEW_PROTOCOLS[previewIdx];

  // PWA install prompt wiring for the "Works Offline" CTA on the landing page.
  // `beforeinstallprompt` fires on supported browsers (Chrome, Edge, newer Safari-on-iOS
  // via Add to Home Screen) when the PWA is not yet installed.
  const [installPromptEvent, setInstallPromptEvent] = useState(null);
  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setInstallPromptEvent(e);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);
  const installReady = !!installPromptEvent;
  const handleInstall = async () => {
    if (!installPromptEvent) return;
    installPromptEvent.prompt();
    const choice = await installPromptEvent.userChoice;
    if (choice?.outcome === "accepted") setInstallPromptEvent(null);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:z-[100] focus:top-3 focus:left-3 focus:bg-[#0B5FFF] focus:text-white focus:px-4 focus:py-2 focus:rounded-md focus:shadow-lg"
        data-testid="skip-to-content"
      >
        Skip to main content
      </a>
      <SiteHeader />

      {/* HERO */}
      <section id="main-content" className="relative overflow-hidden bg-gradient-to-br from-white via-slate-50 to-[#EFF4FF]">
        <div className="absolute inset-0 medical-grid opacity-50" />
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 grid lg:grid-cols-2 gap-14 items-center">
          <div className="animate-rise">
            <Badge className="bg-white border border-brand/40 text-[#111827] mb-5 font-medium shadow-soft px-3 py-1.5" data-testid="hero-badge">
              🇿🇦 Built for South African clinicians
            </Badge>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#111827] leading-[1.05]">
              Rapid, evidence-based
              <br />
              clinical reference
              <br />
              <span className="brand-gradient-text">at the bedside.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
              Protocols, drug dosing, interaction checks and clinical calculators — all calibrated to South African guidelines. Designed for doctors, registrars, nurses and medical students.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={startTrial}
                size="lg"
                className="bg-brand hover:bg-brand-deep text-[#111827] font-semibold rounded-full h-12 px-8 text-base shadow-glow-brand active:scale-[0.98]"
                data-testid="hero-cta-start"
              >
                Start free trial
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button
                onClick={() => nav("/pricing")}
                size="lg"
                variant="outline"
                className="h-12 px-8 text-base border-[#0B5FFF]/30 text-[#0B5FFF] rounded-full hover:bg-[#0B5FFF]/5"
                data-testid="hero-cta-pricing"
              >
                See pricing
              </Button>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-slate-600">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" />No credit card</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" />HPCSA-friendly</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" />Offline-ready</div>
            </div>
          </div>

          <div className="relative animate-rise" style={{ animationDelay: "120ms" }}>
            <div className="absolute -inset-4 bg-gradient-to-br from-[#0B5FFF]/15 via-transparent to-[#14B8A6]/10 blur-2xl rounded-full" />
            <Card className="relative border-slate-200 shadow-xl overflow-hidden">
              <div className="bg-[#0B5FFF] text-white px-5 py-3 flex items-center gap-2 text-xs tracking-wide uppercase">
                <Activity className="w-4 h-4" /> Live Protocol Preview
              </div>
              <CardContent className="p-6 bg-white">
                <div className={`transition-opacity duration-300 ${previewFade ? "opacity-100" : "opacity-0"}`} data-testid="live-protocol-preview">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className={`text-xs tracking-wide uppercase font-semibold ${preview.tagColor}`}>{preview.tag}</div>
                      <h3 className="font-heading font-bold text-xl text-[#111827] mt-1">{preview.title}</h3>
                    </div>
                    <Badge className={`${preview.badge} border`}>Step 1 / {preview.steps.length}</Badge>
                  </div>
                  <ol className="space-y-3">
                    {preview.steps.map((s, i) => (
                      <li key={i} className="flex gap-3">
                        <div className="w-7 h-7 rounded-md bg-[#EFF4FF] text-[#111827] font-semibold flex items-center justify-center text-sm flex-shrink-0">
                          {i + 1}
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900 text-sm">{s.t}</div>
                          <div className="text-sm text-slate-600">{s.d}</div>
                        </div>
                      </li>
                    ))}
                  </ol>
                  <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
                    <span>Source: {preview.source}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Updated 2024</span>
                  </div>
                </div>
                <div className="flex items-center justify-center gap-1.5 mt-4">
                  {PREVIEW_PROTOCOLS.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => { setPreviewFade(false); setTimeout(() => { setPreviewIdx(i); setPreviewFade(true); }, 200); }}
                      aria-label={`Show protocol ${i + 1}`}
                      data-testid={`preview-dot-${i}`}
                      className={`h-1.5 rounded-full transition-all ${i === previewIdx ? "w-6 bg-[#0B5FFF]" : "w-1.5 bg-slate-300 hover:bg-slate-400"}`}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* STATS STRIP — dark navy band with brand serif numerals + count-up */}
      <section className="relative bg-[#1E3A8A] overflow-hidden" data-testid="stats-band">
        <div className="absolute inset-0 ecg-line opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1E3A8A] via-transparent to-[#1E3A8A]" />
        <div className="relative max-w-7xl mx-auto px-6 py-14 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { v: counts?.protocols ?? 517, s: "+", l: "Clinical Protocols" },
            { v: counts?.drugs ?? 397, s: "+", l: "Drug Formulary" },
            { v: counts?.paediatric_drugs ?? 111, s: "", l: "Paed Doses" },
            { v: counts?.interactions ?? 786, s: "+", l: "Interactions" },
          ].map((s, i) => (
            <div key={i} className="text-center">
              <CountUp value={s.v} suffix={s.s} className="font-heading font-bold text-4xl sm:text-5xl text-white" />
              <div className="text-[11px] tracking-[0.2em] uppercase text-white/60 mt-2">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-24">
        <div className="max-w-2xl mb-12">
          <div className="text-xs tracking-widest uppercase text-[#111827] font-semibold">Everything you need</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#111827] mt-2">Clinical workflow, not a textbook.</h2>
          <p className="text-slate-600 mt-3">Every feature is tuned for the shift: fast lookup, clear dosing, actionable guidance — not endless paragraphs.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="feature-grid">
          {features.map((f, i) => {
            const featured = i < 3;
            return (
              <Card
                key={i}
                className={`group relative overflow-hidden transition-all duration-200 hover:-translate-y-1 ${
                  featured
                    ? "border-brand/30 shadow-soft hover:shadow-premium bg-gradient-to-b from-white to-[#F5F9FF]"
                    : "border-slate-200 hover:border-[#0B5FFF]/30 hover:shadow-soft bg-white"
                }`}
              >
                {featured && <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-brand-light via-brand to-brand-deep" />}
                <CardContent className={featured ? "p-7" : "p-6"}>
                  <div className={`rounded-xl flex items-center justify-center mb-4 transition-colors duration-200 ${
                    featured
                      ? "w-12 h-12 bg-[#0B5FFF] text-white"
                      : "w-10 h-10 bg-[#EFF4FF] text-[#0B5FFF] group-hover:bg-[#0B5FFF] group-hover:text-white"
                  }`}>
                    <f.icon className={featured ? "w-6 h-6" : "w-5 h-5"} strokeWidth={1.5} />
                  </div>
                  <h3 className={`font-heading font-semibold text-[#111827] ${featured ? "text-xl" : "text-lg"}`}>{f.title}</h3>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">{f.text}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* FOR CLINICIANS */}
      <section id="clinicians" className="bg-[#1E3A8A] text-white">
        <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs tracking-widest uppercase text-[#93C5FD] font-semibold">For South African clinicians</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mt-2">Built on 500+ South African clinical guidelines, under ongoing clinician review.</h2>
            <p className="text-slate-200 mt-4 leading-relaxed">
              Every protocol, drug monograph and calculator is curated against published South African guidelines (NDoH STG/EML, SAMF, SEMDSA and national specialty societies) and placed under ongoing clinical review — built for medical officers, registrars, nurses and students.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {[
                { i: Stethoscope, t: "General Practice" },
                { i: Activity, t: "Emergency Medicine" },
                { i: Pill, t: "Internal Medicine" },
                { i: Globe, t: "Rural & District" },
              ].map((x, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-md bg-white/10 flex items-center justify-center">
                    <x.i className="w-5 h-5" />
                  </div>
                  <span className="font-medium">{x.t}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="relative rounded-lg overflow-hidden border border-white/10 bg-gradient-to-br from-[#0B5FFF] via-[#1E3A8A] to-[#14B8A6] min-h-[440px] flex items-center justify-center p-10">
            <div className="absolute inset-0 medical-grid opacity-20" />
            <div className="relative grid grid-cols-3 gap-4 w-full max-w-sm">
              {["Emergency", "HIV/TB", "Cardiology", "Paediatrics", "Obstetrics", "Psychiatry", "ICU", "Trauma", "Renal"].map((s) => (
                <div key={s} className="aspect-square rounded-md bg-white/10 border border-white/10 backdrop-blur flex items-center justify-center p-2 text-center">
                  <span className="text-xs font-medium text-white/90">{s}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CREDIBILITY STRIP (real, provable claims — no unverifiable testimonials yet) */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="credibility-section">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-xs tracking-widest uppercase text-[#111827] font-semibold">Why clinicians use it</div>
            <h2 className="font-heading text-3xl font-bold text-[#111827] mt-2">Honest, guideline-anchored reference.</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-5">
            {[
              { icon: BookOpen, n: "500+", l: "SA guidelines referenced across protocols, drugs and calculators" },
              { icon: ShieldCheck, n: "Ongoing", l: "Clinical content placed under continuous clinician review" },
              { icon: Globe, n: "Offline-ready", l: "Installable app built for real South African clinical settings" },
            ].map((x, i) => (
              <Card key={i} className="border-slate-200 bg-white">
                <CardContent className="p-6">
                  <div className="w-10 h-10 rounded-md bg-[#EFF4FF] text-[#111827] flex items-center justify-center mb-4">
                    <x.icon className="w-5 h-5" />
                  </div>
                  <div className="font-heading font-extrabold text-2xl text-[#111827]">{x.n}</div>
                  <p className="text-sm text-slate-600 mt-1 leading-relaxed">{x.l}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="text-center text-sm text-slate-600 mt-8">
            Used RapidCare SA on shift? <Link to="/feedback" className="text-[#111827] font-medium hover:underline" data-testid="credibility-feedback-link">Share your feedback →</Link>
          </p>
        </div>
      </section>

      {/* WORKS OFFLINE */}
      <section className="max-w-7xl mx-auto px-6 py-20" data-testid="works-offline-section">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#EFF4FF] text-[#111827] text-xs font-semibold tracking-wide uppercase mb-5">
              <WifiOff className="w-3 h-3" /> Works Offline
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#111827] leading-tight">
              Install RapidCare SA as an app on your phone or tablet.
            </h2>
            <p className="mt-5 text-slate-700 leading-relaxed">
              Core protocols, drug references, and calculators are available even without signal — built for South Africa's real clinical environments, from regional hospitals with patchy Wi-Fi to rural clinics on mobile data.
            </p>
            <ul className="mt-6 space-y-2 text-sm text-slate-700">
              {[
                "Home-screen icon — full-screen experience, no browser chrome",
                "Offline access to the 500+ protocols and drug monographs you've viewed",
                "Calculators work entirely on-device, no network required",
              ].map((b, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            {installReady && (
              <Button
                onClick={handleInstall}
                className="mt-7 bg-[#0B5FFF] hover:bg-[#0A54E0] h-11 px-5"
                data-testid="landing-install-btn"
              >
                <Download className="w-4 h-4 mr-2" /> Install the app
              </Button>
            )}
            {!installReady && (
              <p className="mt-7 text-xs text-slate-500" data-testid="landing-install-hint">
                On supported browsers (Chrome / Edge / Safari on iOS): use your browser's "Add to Home Screen" menu to install.
              </p>
            )}
          </div>
          <div className="relative">
            <Card className="border-slate-200 shadow-xl bg-white">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 pb-5 border-b border-slate-200">
                  <div className="w-12 h-12 rounded-md bg-[#0B5FFF] text-white flex items-center justify-center">
                    <Activity className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="font-heading font-extrabold text-[#111827]">RapidCare SA</div>
                    <div className="text-[10px] tracking-widest text-slate-500 uppercase">Installed app · Offline ready</div>
                  </div>
                </div>
                <div className="mt-5 grid grid-cols-3 gap-2">
                  {["Protocols", "Drugs", "Calcs", "Paed", "Antibiotics", "Search"].map((x) => (
                    <div key={x} className="aspect-square rounded-md bg-slate-50 border border-slate-200 flex flex-col items-center justify-center gap-1 p-2">
                      <div className="w-8 h-8 rounded-md bg-[#EFF4FF] flex items-center justify-center">
                        <BookOpen className="w-4 h-4 text-[#111827]" />
                      </div>
                      <span className="text-[10px] font-medium text-slate-600">{x}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-5 flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
                  <WifiOff className="w-3.5 h-3.5" /> Offline — last sync 2 min ago
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* SPECIALTIES */}
      <section className="max-w-7xl mx-auto px-6 pt-6 pb-20">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="text-xs tracking-widest uppercase text-[#111827] font-semibold">Covering</div>
          <h2 className="font-heading text-3xl font-bold text-[#111827] mt-2">Specialties & scenarios</h2>
        </div>
        <div className="flex flex-wrap justify-center gap-2">
          {specialties.map((s) => (
            <Badge key={s} className="bg-white border border-slate-200 text-slate-700 hover:border-[#0B5FFF] hover:text-[#111827] px-4 py-2 font-medium cursor-default">
              {s}
            </Badge>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-br from-[#0B5FFF] to-[#1E3A8A] text-white">
        <div className="max-w-5xl mx-auto px-6 py-16 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold">Start your 7-day free trial</h2>
          <p className="mt-4 text-white/90 max-w-2xl mx-auto">No credit card required. Full access to protocols, drugs, calculators and ICD-10 search. You'll never be charged automatically.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={startTrial} size="lg" className="bg-white text-[#111827] hover:bg-slate-100 h-12 px-8 text-base font-semibold" data-testid="cta-signup">
              Start free trial
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <Link to="/pricing">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 h-12 px-8 text-base bg-transparent" data-testid="cta-pricing">
                See pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FOUNDED BY A SA CLINICIAN — WITH SPECIALIST REVIEW */}
      <section className="bg-gradient-to-br from-white via-[#EFF4FF]/40 to-white border-y border-slate-200" data-testid="sole-doctor-section">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <img
            src="/founder.webp"
            alt="Founder — practising South African clinician"
            className="w-24 h-24 rounded-full object-cover object-[center_28%] mx-auto mb-6 ring-4 ring-brand/30 shadow-premium"
            data-testid="founder-avatar"
          />
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-brand/40 text-[#111827] text-xs font-semibold tracking-wide uppercase mb-6 shadow-soft">
            <Stethoscope className="w-3.5 h-3.5 text-brand" /> Built by a practising SA clinician
          </div>
          <div className="relative max-w-3xl mx-auto">
            <span className="absolute -top-6 left-1/2 -translate-x-1/2 font-heading text-7xl text-brand/40 leading-none select-none" aria-hidden="true">&ldquo;</span>
            <h2 className="relative font-heading text-2xl sm:text-3xl lg:text-[2.1rem] font-medium italic text-[#111827] leading-snug pt-4">
              Developed by a practising South African clinician, reviewed on an ongoing basis by healthcare professionals.
            </h2>
          </div>
          <p className="mt-6 text-slate-700 leading-relaxed max-w-2xl mx-auto">
            RapidCare SA is developed independently by a South African clinician. Clinical content is curated against the published South African guidelines listed below (STG/EML, SAMF, SEMDSA and national specialty society recommendations) and is placed under <strong>ongoing review by healthcare professionals</strong>.
          </p>
          <div className="mt-6 inline-flex items-start gap-2 text-xs text-slate-500 bg-white/70 border border-slate-200 rounded-md px-4 py-3 max-w-2xl mx-auto text-left">
            <ShieldCheck className="w-3.5 h-3.5 text-[#111827] flex-shrink-0 mt-0.5" />
            <span>
              <strong>This tool is for reference only and requires a qualified medical practitioner to approve any treatments.</strong> All clinical information should be verified against the current South African Standard Treatment Guidelines and SAMF before being applied to a patient. Final clinical responsibility always remains with the registered healthcare professional using the platform.
            </span>
          </div>
          <div className="mt-8 inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#1E3A8A] text-white text-sm font-medium">
            <Heart className="w-4 h-4 fill-white" />
            Your subscription keeps this platform running, updated, and free of ads.
          </div>
          <div className="mt-8">
            <Link to="/about" className="text-sm text-[#111827] font-medium hover:underline" data-testid="sole-doctor-learn-more">
              Read the full story →
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-4xl mx-auto px-6 py-20">
        <h2 className="font-heading text-3xl font-bold text-[#111827] text-center">Frequently asked questions</h2>
        <div className="mt-8 space-y-4">
          {[
            { q: "Is this a diagnostic tool?", a: "No. RapidCare SA is a clinical reference and decision-support tool. This tool is for reference only and requires a qualified medical practitioner to approve any treatments." },
            { q: "Which guidelines are used?", a: "We prioritise NDoH, SAMF, Stroke SA, SEMDSA, SA Thoracic, SA Heart Association, CCSSA and IMCI. Source is cited on each protocol." },
            { q: "Can I use this on my phone?", a: "Yes — RapidCare SA is a fully responsive web app. Add it to your home screen for a native-like experience." },
            { q: "How are payments handled?", a: "Payments are processed securely via Payfast (ZAR) as a once-off payment for your chosen period. There is no recurring billing and nothing to cancel — your premium access simply expires at the end of the period, and you can renew anytime from Settings." },
            { q: "How does the free trial work?", a: "Every new account starts with a 7-day Premium free trial — full access, no credit card required. When it ends, you're simply moved to the free tier (50 emergency protocols). You are never charged automatically; you only pay if you actively choose a plan." },
            { q: "Do you offer refunds?", a: "No. All payments are final and non-refundable, including for unused time, except where a refund is required by South African consumer law. We offer a 7-day free trial so you can fully evaluate the product before paying — no card is charged during the trial." },
            { q: "Is my data private?", a: "We store only your account, favorites, and viewing history. We never store patient-identifiable information." },
          ].map((f, i) => (
            <Card key={i} className="border-slate-200">
              <CardContent className="p-5">
                <h3 className="font-heading font-semibold text-[#111827]">{f.q}</h3>
                <p className="text-sm text-slate-600 mt-2">{f.a}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
